import stateManager from '../shared/StateManager.mjs';
import viewManager from '../shared/ViewManager.mjs';
console.log('[Tracer] index.mjs loaded');

// Lightweight boot marker (can be removed later)
window.__APP_BUILD_INFO = { startedAt: new Date().toISOString() };
console.log('[BOOT] app bundle start', window.__APP_BUILD_INFO.startedAt);

// src/app/index.mjs - Core navigation and routing only
class RiverboatApp {
    constructor() {
        console.log('[Tracer] RiverboatApp constructor called');
        this.currentPage = window.appData?.currentPage || 'maps';
        this.siteID = window.appData?.siteID || 'regional';
        this.siteName = window.appData?.siteName || '';
        this.range = window.appData?.range || 20;
        this.brand = window.appData?.brand || 'riverboat-tracker';
        this.hasExplicitRangeInPath = this.checkForExplicitRangeInPath();
        
        // Module management
        this.loadedModules = new Map();
        this.currentModule = null;
        
        this.init();
    }

    checkForExplicitRangeInPath() {
        const appPath = window.location.pathname.replace(/^\/app\/?/, '');
        const parts = appPath.split('/').filter(Boolean);

        return /^\d+$/.test(parts[1] || '');
    }

    async init() {
    console.log('🚀 STEP 1: Starting RiverboatApp initialization');
    
      try {
         console.log('🚀 STEP 2: Initializing state manager...');
         await this.initializeStateManager();
         console.log('✅ STEP 2: State manager initialized');

         console.log('🚀 STEP 3: Setting up event listeners...');
         this.setupEventListeners();
         console.log('✅ STEP 3: Event listeners setup');

         console.log('🚀 STEP 4: Setting up tooltips...');
         this.setupTooltips();
         console.log('✅ STEP 4: Tooltips setup');

         console.log('🚀 STEP 5: Updating site info...');
         this.updateSiteInfo();
         console.log('✅ STEP 5: Site info updated');

                 console.log('🚀 STEP 6: Showing page:', this.currentPage);
                 try {
                     await this.showPage(this.currentPage);
                     console.log('✅ STEP 6: Page shown');
                 } catch(e) {
                     console.error('❌ showPage failed at init', e);
                 }

         console.log('🚀 STEP 7: Updating navigation...');
         this.updateActiveNavButton();
         console.log('✅ STEP 7: Navigation updated');

         console.log('🎉 RiverboatApp initialization COMPLETE!');
      } catch (error) {
         console.error('💥 RiverboatApp initialization FAILED:', error);
      }
    }

async initializeStateManager() {
        try {
            // Initialize state manager with app data
            const initialState = {
                currentPage: this.currentPage,
                viewMode: 'tiles', // Default view mode
                mapMode: 'roadmap', // Default map mode
                mobileMenuOpen: false,
                announcementsEnabled: true // Default announcements on
            };
            
            // Set initial state if not already set
            Object.keys(initialState).forEach(key => {
                if (stateManager.getState(key) === undefined) {
                    stateManager.setState({ [key]: initialState[key] });
                }
            });
            
            // Initialize view manager
            viewManager.initialize();
            
            // Subscribe to state changes
            this.setupStateSubscriptions();
            
            console.log('State management initialized');
        } catch (error) {
            console.warn('Failed to initialize state management:', error);
        }
    }

    setupStateSubscriptions() {
        // Subscribe to view mode changes
        stateManager.subscribe('viewMode', (viewMode) => {
            this.updateViewModeButtons(viewMode);
            if (this.currentModule && this.currentModule.setViewMode) {
                this.currentModule.setViewMode(viewMode);
            }
        });

        // Subscribe to map mode changes
        stateManager.subscribe('mapMode', (mapMode) => {
            this.updateMapTypeButtons(mapMode);
            if (this.currentModule && this.currentModule.setMapType) {
                this.currentModule.setMapType(mapMode);
            }
        });

        // Subscribe to mobile menu changes
        stateManager.subscribe('mobileMenuOpen', (isOpen) => {
            const navMenu = document.getElementById('nav-menu');
            if (navMenu) {
                navMenu.classList.toggle('mobile-open', isOpen);
            }
        });

        // Subscribe to announcements changes
        stateManager.subscribe('announcementsEnabled', (isEnabled) => {
            this.updateAnnouncementButtons(isEnabled);
            // Notify current module if it handles announcements
            if (this.currentModule && this.currentModule.setAnnouncementsEnabled) {
                this.currentModule.setAnnouncementsEnabled(isEnabled);
            }
        });
    }

    setupEventListeners() {
        // Navigation button clicks
        document.querySelectorAll('.nav-button').forEach(button => {
            button.addEventListener('click', (e) => {
                const page = e.target.dataset.page;
                if (page) {
                    this.navigateToPage(page);
                }
            });
        });

        // Mobile menu toggle - updated to use state manager
        const mobileToggle = document.getElementById('mobile-toggle');
        
        if (mobileToggle) {
            mobileToggle.addEventListener('click', () => {
                const currentState = stateManager.getState('mobileMenuOpen');
                stateManager.setState({ mobileMenuOpen: !currentState });
            });
        }

        // Handle browser back/forward
        window.addEventListener('popstate', (e) => {
            if (e.state && e.state.page) {
                this.showPage(e.state.page, false);
                this.updateActiveNavButton();
            }
        });

        // Settings dialog controls
        this.setupSettingsDialog();
    }

    setupTooltips() {
        const tooltip = document.getElementById('nav-tooltip');
        let tooltipTimeout;
        
        document.querySelectorAll('.nav-button').forEach(button => {
            button.addEventListener('mouseenter', (e) => {
                if (tooltipTimeout) {
                    clearTimeout(tooltipTimeout);
                }
                
                const tooltipText = e.target.dataset.tooltip;
                if (tooltipText && window.innerWidth > 768) {
                    tooltip.textContent = tooltipText;
                    tooltip.style.display = 'block';
                    
                    const rect = e.target.getBoundingClientRect();
                    tooltip.style.left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2) + 'px';
                    tooltip.style.top = rect.bottom + 5 + 'px';
                    
                    setTimeout(() => tooltip.classList.add('show'), 10);
                }
            });
            
            button.addEventListener('mouseleave', () => {
                tooltipTimeout = setTimeout(() => {
                    tooltip.classList.remove('show');
                    setTimeout(() => {
                        tooltip.style.display = 'none';
                    }, 300);
                }, 500);
            });
        });
    }

    async navigateToPage(page) {
        // Cleanup current module
        if (this.currentModule && this.currentModule.cleanup) {
            this.currentModule.cleanup();
        }

        // Hide mobile live count when leaving Maps page
        const mobileLiveCount = document.getElementById('mobile-live-count');
        if (mobileLiveCount && this.currentPage === 'maps' && page !== 'maps') {
            mobileLiveCount.style.display = 'none';
        }

        this.currentPage = page;
        await this.showPage(page);
        this.updateActiveNavButton();
        this.updateURL();
        
        const navMenu = document.getElementById('nav-menu');
        if (navMenu) {
            navMenu.classList.remove('mobile-open');
        }
    }

    async showPage(page, pushState = true) {
        // Hide all pages
        document.querySelectorAll('.page-template').forEach(template => {
            template.style.display = 'none';
        });
        
        // Show loading indicator
        this.showLoadingIndicator(page);
        
        try {
            // Load page module
            await this.loadPageModule(page);
            
            // Show target page
            const targetPage = document.getElementById(`${page}-page`);
            if (targetPage) {
                targetPage.style.display = 'block';
            }
            
            this.hideLoadingIndicator();
            
        } catch (error) {
            console.error(`Failed to load page ${page}:`, error);
            this.showErrorMessage(page, error);
        }
        
        // Handle sub-navigation
        this.updateSubNavigation(page);
        
        // Push state to history
        if (pushState) {
            const url = this.buildURL(page);
            history.pushState({ page: page }, '', url);
        }
    }

    // Update the loadPageModule method to work with state management
    async loadPageModule(page) {
        // Check if module is already loaded
        if (this.loadedModules.has(page)) {
            this.currentModule = this.loadedModules.get(page);
            
            // Set the current view module for the view manager
            viewManager.setCurrentViewModule(this.currentModule);
            
            // Apply current state to the module
            this.applyCurrentStateToModule();
            return;
        }

        let ModuleClass;
        let ActivityClass; 
        
    // Dynamic import based on page - fix the paths
    switch(page) {
        case 'maps':
            console.log('[ModuleLoader] dynamic import maps module');
            // Dynamically import the maps module (enables code-splitting again)
            ({ MapsModule: ModuleClass } = await import('./modules/maps.mjs'));
            break;
        case 'activity':

            ({ ActivityModule: ModuleClass } = await import('./modules/activity.mjs'));
            break;
        case 'history':
            ({ HistoryModule: ModuleClass } = await import('./modules/history.mjs'));
            break;
        case 'waypoints':
            this.showPlaceholderPage('waypoints');
            return;
        case 'other':
            ({ OtherModule: ModuleClass } = await import('./modules/other.mjs'));
            break;
        default:
            throw new Error(`Unknown page: ${page}`);
    }

        // Create and initialize module
        const moduleInstance = new ModuleClass(this);
        await moduleInstance.initialize();
        
        // Set the current view module for the view manager
        viewManager.setCurrentViewModule(moduleInstance);
        
        // Cache the module
        this.loadedModules.set(page, moduleInstance);
        this.currentModule = moduleInstance;
        
        // Apply current state to the newly loaded module
        this.applyCurrentStateToModule();
    }

    //New method to apply current state to the module
        applyCurrentStateToModule() {
        if (!this.currentModule) return;
        
        // Apply current view mode
        const viewMode = stateManager.getState('viewMode');
        if (viewMode && this.currentModule.setViewMode) {
            this.currentModule.setViewMode(viewMode);
        }
        
        // Apply current map mode
        const mapMode = stateManager.getState('mapMode');
        if (mapMode && this.currentModule.setMapType) {
            this.currentModule.setMapType(mapMode);
        }
    }

    // Update settings button methods to use state manager
    setupSettingsButtons() {
        // Generic listeners for any map/view mode buttons (settings dialog + inline submenu)
        document.querySelectorAll('[data-map-mode]').forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = btn.getAttribute('data-map-mode');
                stateManager.setState({ mapMode: mode });
            });
        });

        document.querySelectorAll('[data-view-mode]').forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = btn.getAttribute('data-view-mode');
                stateManager.setState({ viewMode: mode });
            });
        });
        
        // Announcements toggle
        const announcementsBtn = document.getElementById('toggle-announcements');
        if (announcementsBtn) {
            announcementsBtn.addEventListener('click', () => {
                this.toggleAnnouncements();
            });
        }
    }

    showLoadingIndicator(page) {
        const targetPage = document.getElementById(`${page}-page`);
        if (targetPage) {
            const loadingDiv = document.createElement('div');
            loadingDiv.className = 'page-loading';
            loadingDiv.innerHTML = `
                <div class="loading-spinner"></div>
                <p>Loading ${page}...</p>
            `;
            targetPage.appendChild(loadingDiv);
        }
    }

    showPlaceholderPage(pageName) {
      const targetPage = document.getElementById(`${pageName}-page`);
      if (targetPage) {
         targetPage.innerHTML = `
               <div class="placeholder-page">
                  <h2>${pageName.charAt(0).toUpperCase() + pageName.slice(1)} Page</h2>
                  <p>This page is under development.</p>
               </div>
         `;
         targetPage.style.display = 'block';
      }
   }

    hideLoadingIndicator() {
        document.querySelectorAll('.page-loading').forEach(el => el.remove());
    }

    showErrorMessage(page, error) {
        const targetPage = document.getElementById(`${page}-page`);
        if (targetPage) {
            targetPage.innerHTML = `
                <div class="error-message">
                    <h3>Failed to load ${page}</h3>
                    <p>Error: ${error.message}</p>
                    <button onclick="location.reload()">Retry</button>
                </div>
            `;
            targetPage.style.display = 'block';
        }
    }

    updateSubNavigation(page) {
        const subNav = document.getElementById('sub-nav');
        const subNavContent = document.getElementById('sub-nav-content');
        
        if (this.currentModule && this.currentModule.getSubNavigation) {
            const subMenuItems = this.currentModule.getSubNavigation();
            
            if (subMenuItems && subMenuItems.length > 0) {
                subNavContent.innerHTML = '';
                subMenuItems.forEach(item => {
                    const button = document.createElement('button');
                    button.className = 'sub-nav-button';
                    button.textContent = item.label;
                    button.dataset.action = item.action;
                    button.addEventListener('click', (e) => {
                        this.handleSubNavAction(item.action, e.currentTarget);
                    });
                    subNavContent.appendChild(button);
                });
                subNav.style.display = 'block';
                return;
            }
        }
        
        subNav.style.display = 'none';
    }

    handleSubNavAction(action, clickedButton = null) {
        // Remove active class from all sub-nav buttons
        document.querySelectorAll('.sub-nav-button').forEach(btn => {
            btn.classList.remove('active');
        });
        
        // Add active class to clicked button
        if (clickedButton) {
            clickedButton.classList.add('active');
        }
        
        // Delegate to current module
        if (this.currentModule && this.currentModule.handleSubNavAction) {
            this.currentModule.handleSubNavAction(action);
        }
    }

    updateActiveNavButton() {
        document.querySelectorAll('.nav-button').forEach(btn => {
            btn.classList.remove('active');
        });
        
        const activeBtn = document.querySelector(`[data-page="${this.currentPage}"]`);
        if (activeBtn) {
            activeBtn.classList.add('active');
        }
    }

    // Utility methods
    updateSiteInfo() {
        const siteLabel = document.getElementById('site-label');
        const rangeValue = document.getElementById('range-value');
        
        // Mobile versions
        const mobileSiteLabel = document.getElementById('mobile-site-label');
        const mobileRangeValue = document.getElementById('mobile-range-value');
        
        const siteName = this.siteName || this.siteID || 'Unknown Area';
        
        // Update desktop version
        if (siteLabel) {
            siteLabel.textContent = siteName;
        }
        
        if (rangeValue) {
            rangeValue.textContent = this.range;
        }
        
        // Update mobile version
        if (mobileSiteLabel) {
            mobileSiteLabel.textContent = siteName;
        }
        
        if (mobileRangeValue) {
            mobileRangeValue.textContent = this.range;
        }
    }

    buildURL(page = this.currentPage) {
        const basePath = '/app';
        const parts = [basePath];
        
        if (this.siteID && this.siteID !== 'regional') {
            parts.push(this.siteID);
        }
        
        const shouldIncludeRange = this.range && (this.range !== 20 || this.hasExplicitRangeInPath);
        if (shouldIncludeRange) {
            parts.push(this.range.toString());
        }
        
        if (page && page !== 'maps') {
            parts.push(page);
        }
        
        return parts.join('/');
    }

    updateURL() {
        const url = this.buildURL();
        history.replaceState({ page: this.currentPage }, '', url);
    }

    changeSite(site) {
        console.log(`Changing to site: ${site}`);
        this.siteID = site;
        this.siteName = '';
        this.updateSiteInfo();
        this.updateURL();
        
        // Refresh current module with new site
        if (this.currentModule && this.currentModule.onSiteChange) {
            this.currentModule.onSiteChange(site);
        }
    }

    // Settings dialog functionality
    setupSettingsDialog() {
        const settingsToggle = document.getElementById('settings-toggle');
        const settingsOverlay = document.getElementById('settings-overlay');
        const settingsClose = document.getElementById('settings-close');
        
        if (settingsToggle && settingsOverlay) {
            // Open settings dialog
            settingsToggle.addEventListener('click', (e) => {
                e.preventDefault();
                settingsOverlay.classList.add('active');
                document.body.style.overflow = 'hidden'; // Prevent background scrolling
            });
            
            // Close settings dialog
            const closeDialog = () => {
                settingsOverlay.classList.remove('active');
                document.body.style.overflow = ''; // Restore scrolling
            };
            
            if (settingsClose) {
                settingsClose.addEventListener('click', closeDialog);
            }
            
            // Close on overlay click (outside dialog)
            settingsOverlay.addEventListener('click', (e) => {
                if (e.target === settingsOverlay) {
                    closeDialog();
                }
            });
            
            // Close on Escape key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && settingsOverlay.classList.contains('active')) {
                    closeDialog();
                }
            });
        }
        
        // Setup settings button event listeners
        this.setupSettingsButtons();
    }
    
  
    
    setMapType(type) {
        stateManager.setState({ mapMode: type });
    }
    
    setViewMode(mode) {
        stateManager.setState({ viewMode: mode });
    }
    
    toggleAnnouncements() {
        const currentState = stateManager.getState('announcementsEnabled');
        stateManager.setState({ announcementsEnabled: !currentState });
    }
    
    updateMapTypeButtons(activeType = null) {
        const currentType = activeType || stateManager.getState('mapMode');
        document.querySelectorAll('[data-map-mode]').forEach(btn => {
            const mode = btn.getAttribute('data-map-mode');
            btn.classList.toggle('active', mode === currentType);
        });
    }
    
    updateViewModeButtons(activeMode = null) {
        const currentMode = activeMode || stateManager.getState('viewMode');
        document.querySelectorAll('[data-view-mode]').forEach(btn => {
            const mode = btn.getAttribute('data-view-mode');
            btn.classList.toggle('active', mode === currentMode);
        });
    }
    
    updateAnnouncementButtons(isEnabled = null) {
        const enabled = isEnabled !== null ? isEnabled : stateManager.getState('announcementsEnabled');
        
        // Update desktop toggle
        const desktopToggle = document.getElementById('toggle-announcements');
        const desktopLed = document.getElementById('announcements-led');
        
        if (desktopToggle) {
            desktopToggle.classList.toggle('active', enabled);
        }
        if (desktopLed) {
            desktopLed.classList.toggle('active', enabled);
        }
        
        // Update mobile toggle
        const mobileToggle = document.getElementById('mobile-toggle-announcements');
        const mobileLed = document.getElementById('mobile-announcements-led');
        
        if (mobileToggle) {
            mobileToggle.classList.toggle('active', enabled);
        }
        if (mobileLed) {
            mobileLed.classList.toggle('active', enabled);
        }
    }

    // Cleanup
    destroy() {
        if (this.currentModule && this.currentModule.cleanup) {
            this.currentModule.cleanup();
        }
    }
}
//End of RiverboatApp class



// If Google Maps is already loaded (e.g., cached), initialize immediately
if (window.google && window.google.maps) {
    window.app = new RiverboatApp();
} else {
    window.initMap = function() {
        window.app = new RiverboatApp();
    };
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
    if (window.app) {
        window.app.destroy();
    }
});

window.RiverboatApp = RiverboatApp;
